import { useEffect, useState, useRef } from 'react';
import { defaultProfileImage } from '../constants/defaultProfileImage';
import { getBeltForSessions, BELT_RANKS } from '../constants/beltRanks';
import { APP_VERSION } from '../constants/appVersion';
import { useFriends } from '../hooks/useFriends';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { playTone, ensureAudioUnlocked, playButtonClick } from '../utils/audio';

export default function HomePage({ user, darkMode, onDarkModeToggle, onLogSession, onViewProfile, onLogout, onProfile }) {
  const [stats, setStats] = useState({});
  const [friendStats, setFriendStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const { friends, incomingRequests, sendRequest, acceptRequest, declineRequest, error: friendsError, findUserByUsername } = useFriends(user || {});
  const [addUsername, setAddUsername] = useState('');
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReqModal, setShowReqModal] = useState(false);
  const [showRanksModal, setShowRanksModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [headerOpacity, setHeaderOpacity] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [isAddingFriend, setIsAddingFriend] = useState(false);
  const friendsSectionRef = useRef(null);
  const lastStatsRef = useRef({ sessions: null, workouts: null });

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const newOpacity = Math.max(0, 1 - scrollY / 200);
      setHeaderOpacity(newOpacity);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadFriendStats = async () => {
      if (!friends || friends.length === 0) return;
      const statsMap = {};
      for (const friend of friends) {
        try {
          const profileRef = doc(db, 'profiles', friend.uid);
          const profileSnap = await getDoc(profileRef);
          const profileData = profileSnap.data() || {};
          const sessions = profileData.sessionCount || 0;
          const beltName = profileData.beltRank || getBeltForSessions(sessions).name;
          statsMap[friend.uid] = { sessionCount: sessions, beltName };
        } catch (err) {
          console.error('Failed to load friend stats', err);
        }
      }
      setFriendStats(statsMap);
    };
    loadFriendStats();
  }, [friends]);

  useEffect(() => {
    const loadSessions = async () => {
      if (!user?.uid) {
        setLoadingStats(false);
        return;
      }
      setLoadingStats(true);
      try {
        const profileRef = doc(db, 'profiles', user.uid);
        const profileSnap = await getDoc(profileRef);
        const profileData = profileSnap.data() || {};

        setStats({
          sessionCount: profileData.sessionCount || 0,
          workoutCount: profileData.workoutCount || 0
        });
      } catch (err) {
        console.error('Failed to load stats', err);
        setStats({ sessionCount: 0, workoutCount: 0 });
      } finally {
        setLoadingStats(false);
        // Minimum loading time for smoother UX
        setTimeout(() => setInitialLoading(false), 800);
      }
    };
    loadSessions();
  }, [user]);

  const userSessions = stats.sessionCount || 0;
  const userWorkouts = stats.workoutCount || 0;
  const profileImageSrc = user?.profileImage || defaultProfileImage;
  const username = user?.username || user?.displayName || 'Athlete';
  const userBelt = getBeltForSessions(userSessions);

  const getBeltGradientText = (beltColor, fontSize) => ({
    fontSize,
    fontWeight: 'bold',
    backgroundImage: `linear-gradient(to bottom, #ffffff 0%, #ffffff 5%, ${beltColor} 100%)`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: '#ffffff',
    display: 'inline-block',
    filter: 'drop-shadow(-1px -1px 0 #000) drop-shadow(1px -1px 0 #000) drop-shadow(-1px 1px 0 #000) drop-shadow(1px 1px 0 #000)'
  });



  const playStatIncrease = (count = 1) => {
    const base = 820;
    for (let i = 0; i < count; i += 1) {
      setTimeout(() => playTone(base + i * 120, 0.07, 0.05), i * 90);
    }
  };

  const playTestSound = async () => {
    await ensureAudioUnlocked();
    await playTone(523.25, 0.1, 0.08);
    setTimeout(() => playTone(659.25, 0.1, 0.08), 140);
    setTimeout(() => playTone(783.99, 0.14, 0.1), 280);
  };

  // Spinner removed on HomePage; we fade numbers in after loading

  const cardStyle = {
    backgroundColor: '#111827',
    borderRadius: '8px',
    padding: '24px',
    border: '2px solid #374151',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s'
  };
  const headerStyle = {
    backgroundColor: darkMode ? '#1f2937' : '#ffffff',
    borderBottom: '1px solid ' + (darkMode ? '#374151' : '#e5e7eb'),
    padding: '16px',
    position: 'sticky',
    top: 0,
    zIndex: 20,
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
  };

  useEffect(() => {
    const unlock = () => { ensureAudioUnlocked(); };
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);

  useEffect(() => {
    if (loadingStats) return;
    const prev = lastStatsRef.current;
    const sessionsIncreased = prev.sessions !== null && userSessions > prev.sessions;
    const workoutsIncreased = prev.workouts !== null && userWorkouts > prev.workouts;

    if (sessionsIncreased || workoutsIncreased) {
      const soundCount = (sessionsIncreased ? 1 : 0) + (workoutsIncreased ? 1 : 0);
      playStatIncrease(soundCount || 1);
    }

    lastStatsRef.current = { sessions: userSessions, workouts: userWorkouts };
  }, [loadingStats, userSessions, userWorkouts]);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'rgba(15, 23, 42, 0.65)',
      color: '#f9fafb'
    }}>
      <style>{`
        @keyframes pulseGlow {
          0% { transform: scale(1); opacity: 0.9; }
          70% { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
      `}</style>
      {/* Loading Animation */}
      {initialLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          animation: 'fadeOut 0.5s ease-out forwards',
          animationDelay: '0.5s'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #374151',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            marginTop: '24px',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#3b82f6',
            textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
          }}>
            Loading FitBudly...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes fadeOut {
              to {
                opacity: 0;
                visibility: hidden;
              }
            }
          `}</style>
        </div>
      )}
      
      {/* Fading Title */}
      <div style={{
        textAlign: 'center',
        padding: '20px 16px 4px',
        transition: 'opacity 0.3s ease',
        opacity: headerOpacity,
        position: 'relative'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '900',
          marginBottom: '4px',
          letterSpacing: '3px',
          textTransform: 'uppercase',
          color: '#f9fafb',
          textShadow: '-2px -2px 0 #000, 2px -2px 0 #000, -2px 2px 0 #000, 2px 2px 0 #000, 0 0 18px rgba(59,130,246,0.7)',
          filter: 'drop-shadow(-1px -1px 0 #000) drop-shadow(1px -1px 0 #000) drop-shadow(-1px 1px 0 #000) drop-shadow(1px 1px 0 #000)'
        }}>
          FitBudly
        </h1>
        <div style={{ textAlign: 'center', fontSize: '13px', color: '#f9fafb', marginBottom: '6px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000', background: 'rgba(59,130,246,0.7)', borderRadius: '6px', padding: '2px 8px', fontWeight: 'bold', display: 'inline-block' }}>
          Version {APP_VERSION}
        </div>
        <div style={{ textAlign: 'center', fontSize: '14px', color: '#f9fafb', marginBottom: '4px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
          Logged in as <strong>{username}</strong>
        </div>
      </div>

      <hr style={{ margin: '6px 16px 12px', borderColor: '#374151' }} />

      {/* Main Content */}

      {/* Action Buttons */}
      <div style={{ padding: '0 16px 12px', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => { playButtonClick(); setShowInfoModal(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '35px',
                height: '35px',
                padding: '0',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
              aria-label="Info"
            >
              ℹ️
            </button>
            <button
              onClick={() => { playButtonClick(); onProfile(); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '8px 12px',
                backgroundColor: '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                minWidth: '104px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              👤 Profile
            </button>
            <button
              onClick={() => { playButtonClick(); setShowRanksModal(true); }}
              style={{
                padding: '8px 12px',
                backgroundColor: '#0ea5e9',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                minWidth: '104px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              🏆 Ranks
            </button>
            <button
              onClick={() => { playButtonClick(); handleLogout(); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '35px',
                height: '35px',
                padding: '0',
                backgroundColor: '#ef4444',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '16px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
              aria-label="Logout"
            >
              🔌
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', flexWrap: 'nowrap' }}>
            <button
              onClick={() => { playButtonClick(); friendsSectionRef.current?.scrollIntoView({ behavior: 'smooth' }); }}
              style={{
                padding: '8px 10px',
                backgroundColor: '#8b5cf6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                minWidth: '100px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              👥 Friends
            </button>
            <button
              onClick={() => { playButtonClick(); setShowAddModal(true); }}
              style={{
                padding: '8px 10px',
                backgroundColor: '#8b5cf6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                minWidth: '110px',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              🤝 Add Friend
            </button>
            <button
              onClick={() => { playButtonClick(); setShowReqModal(true); }}
              style={{
                padding: '8px 10px',
                backgroundColor: incomingRequests.length > 0 ? '#8b5cf6' : '#9ca3af',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                minWidth: '120px',
                position: 'relative',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              📬 Requests ({incomingRequests.length})
              {incomingRequests.length > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: '#facc15',
                    boxShadow: '0 0 8px rgba(250, 204, 21, 0.9)'
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(250, 204, 21, 0.7)',
                      animation: 'pulseGlow 1.2s ease-out infinite'
                    }}
                  />
                </span>
              )}
            </button>

          </div>
        </div>
      </div>

      <hr style={{ margin: '6px 16px 10px', borderColor: '#374151' }} />

      {/* Content */}
      <div style={{ padding: '0 12px 24px' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          {/* Me Card */}
          <div style={{ marginBottom: '8px' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#f9fafb',
              marginBottom: '8px',
              textAlign: 'center',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              Me
            </h2>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                onClick={() => { playButtonClick(); onProfile(); }}
                style={{
                  ...cardStyle,
                  width: '192px',
                  border: '2px solid #60a5fa',
                  backgroundColor: darkMode ? '#1f2937' : '#0f172a',
                  backgroundImage: `linear-gradient(135deg, ${userBelt.color}33, rgba(15,23,42,0.8)), radial-gradient(circle at 20% 20%, ${userBelt.color}33, transparent 55%), radial-gradient(circle at 80% 10%, ${userBelt.color}2B, transparent 55%)`,
                  borderRadius: '8px',
                  padding: '20px',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 0 0 2px rgba(0,0,0,0.35), 0 12px 30px rgba(0,0,0,0.25)',
                  animation: 'scaleIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both'
                }}
                onMouseEnter={(e) => e.target.style.borderColor = '#3b82f6'}
                onMouseLeave={(e) => e.target.style.borderColor = '#60a5fa'}
              >
                {/* Banner shine effect */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: 'linear-gradient(120deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.0) 35%, rgba(255,255,255,0.06) 70%, rgba(255,255,255,0.0) 100%)',
                  opacity: darkMode ? 0.6 : 0.4,
                  pointerEvents: 'none'
                }} />
                {/* Top right glow */}
                <div style={{
                  position: 'absolute',
                  top: '-60px',
                  right: '-80px',
                  width: '200px',
                  height: '200px',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${userBelt.color}59 0%, ${userBelt.color}00 70%)`,
                  pointerEvents: 'none'
                }} />
                {/* Bottom left glow */}
                <div style={{
                  position: 'absolute',
                  bottom: '-70px',
                  left: '-60px',
                  width: '220px',
                  height: '220px',
                  borderRadius: '50%',
                  background: `radial-gradient(circle, ${userBelt.color}47 0%, ${userBelt.color}00 70%)`,
                  pointerEvents: 'none'
                }} />
                {/* Top shine line */}
                <div style={{
                  position: 'absolute',
                  top: '18px',
                  left: '-40px',
                  width: '180px',
                  height: '6px',
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.0), rgba(255,255,255,0.35), rgba(255,255,255,0.0))',
                  transform: 'rotate(-12deg)',
                  opacity: 0.6,
                  pointerEvents: 'none'
                }} />
                {/* Bottom shine line */}
                <div style={{
                  position: 'absolute',
                  bottom: '26px',
                  right: '-30px',
                  width: '180px',
                  height: '6px',
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.0), rgba(255,255,255,0.35), rgba(255,255,255,0.0))',
                  transform: 'rotate(12deg)',
                  opacity: 0.6,
                  pointerEvents: 'none'
                }} />
                
                {/* Content */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ position: 'relative', width: '96px', height: '96px', margin: '0 auto 16px' }}>
                  <div style={{
                    position: 'absolute',
                    left: '50%',
                    top: '52%',
                    transform: 'translate(-50%, -50%)',
                    width: '156px',
                    height: '12px',
                    borderRadius: '8px',
                    backgroundColor: userBelt.color,
                    border: userBelt.name === 'White Belt' ? '2px solid #000000' : '2px solid #ffffff',
                    boxShadow: '0 0 8px rgba(0,0,0,0.4)',
                    zIndex: 0,
                    backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 4px, rgba(255,255,255,0) 4px 8px)'
                  }}>
                    <span style={{
                      position: 'absolute',
                      right: '12px',
                      top: '0',
                      bottom: '0',
                      width: '16px',
                      backgroundColor: userBelt.stripe,
                      borderLeft: '2px solid #000000'
                    }} />
                  </div>
                  <img src={profileImageSrc} alt={username} style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: `2px solid ${darkMode ? '#ffffff' : '#000000'}`,
                    position: 'relative',
                    zIndex: 1,
                    margin: '0 auto',
                    display: 'block'
                  }} />
                  <div style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: '2px',
                    transform: 'translateX(-50%)',
                    padding: '2px 8px',
                    backgroundColor: 'rgba(0,0,0,0.65)',
                    border: '1px solid #000000',
                    borderRadius: '999px',
                    color: '#ffffff',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
                    zIndex: 2
                  }}>
                    {userBelt.name}
                  </div>
                </div>
                <p style={{
                  fontWeight: 'bold',
                  color: '#f9fafb',
                  marginBottom: '10px',
                  textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
                  filter: 'drop-shadow(-1px -1px 0 #000) drop-shadow(1px -1px 0 #000) drop-shadow(-1px 1px 0 #000) drop-shadow(1px 1px 0 #000)'
                }}>
                  {user?.username}
                </p>
                <div>
                  <p style={{
                    minHeight: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: loadingStats ? 0 : 1,
                    transition: 'opacity 0.25s ease'
                  }}>
                    <span style={getBeltGradientText(userBelt.color, '32px')}>
                      {loadingStats ? '—' : userSessions}
                    </span>
                  </p>
                  <p style={{ fontSize: '12px', color: '#f9fafb', marginBottom: '8px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                    Sessions
                  </p>
                  <p style={{ fontSize: '26px', fontWeight: 'bold', color: '#ef4444', minHeight: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: loadingStats ? 0 : 1, transition: 'opacity 0.25s ease', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                    {loadingStats ? '—' : userWorkouts}
                  </p>
                  <p style={{ fontSize: '12px', color: '#ffffff', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                    Total Workouts
                  </p>
                </div>
                </div>
              </button>
            </div>
          </div>

          {/* Log Button */}
          <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>
            <button
              onClick={() => { playButtonClick(); onLogSession(); }}
              style={{
                width: '100%',
                maxWidth: '320px',
                padding: '16px 24px',
                backgroundColor: '#22c55e',
                color: '#ffffff',
                fontSize: '18px',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                boxShadow: '0 10px 15px rgba(34, 197, 94, 0.3)',
                textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
              }}
            >
              ➕ New Session
            </button>
          </div>

          <hr style={{ margin: '12px 0', borderColor: '#374151' }} />

          {/* Friends */}
          <div ref={friendsSectionRef}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#f9fafb',
              marginBottom: '24px',
              textAlign: 'center',
              textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
            }}>
              Friends
            </h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '16px',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              {friends.length === 0 && (
                <p style={{ textAlign: 'center', gridColumn: '1/-1', color: '#f9fafb', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                  No friends yet
                </p>
              )}
              {friends.map((friend, index) => (
                <button
                  key={friend.uid || friend.id}
                  onClick={() => { playButtonClick(); onViewProfile(friend); }}
                  style={{
                    ...cardStyle,
                    animation: `scaleIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.2 + index * 0.1}s both`
                  }}
                  onMouseEnter={(e) => e.target.style.borderColor = '#3b82f6'}
                  onMouseLeave={(e) => e.target.style.borderColor = darkMode ? '#374151' : '#e5e7eb'}
                >
                  {(() => {
                    const friendBelt = (BELT_RANKS.find(b => b.name === friendStats[friend.uid]?.beltName) || getBeltForSessions(0));
                    return (
                      <div style={{ position: 'relative', width: '96px', height: '96px', margin: '0 auto 16px' }}>
                        <div style={{
                          position: 'absolute',
                          left: '50%',
                          top: '52%',
                          transform: 'translate(-50%, -50%)',
                          width: '156px',
                          height: '12px',
                          borderRadius: '8px',
                          backgroundColor: friendBelt.color,
                          border: friendBelt.name === 'White Belt' ? '2px solid #000000' : '2px solid #ffffff',
                          boxShadow: '0 0 8px rgba(0,0,0,0.4)',
                          zIndex: 0,
                          backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 4px, rgba(255,255,255,0) 4px 8px)'
                        }}>
                          <span style={{
                            position: 'absolute',
                            right: '12px',
                            top: '0',
                            bottom: '0',
                            width: '16px',
                            backgroundColor: friendBelt.stripe,
                            borderLeft: '2px solid #000000'
                          }} />
                        </div>
                        <img src={friend.profileImage || defaultProfileImage} alt={friend.username || friend.displayName || 'Friend'} style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: `2px solid ${darkMode ? '#ffffff' : '#000000'}`,
                          position: 'relative',
                          zIndex: 1,
                          margin: '0 auto',
                          display: 'block'
                        }} />
                        <div style={{
                          position: 'absolute',
                          left: '50%',
                          bottom: '2px',
                          transform: 'translateX(-50%)',
                          padding: '2px 8px',
                          backgroundColor: 'rgba(0,0,0,0.65)',
                          border: '1px solid #000000',
                          borderRadius: '999px',
                          color: '#ffffff',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          textTransform: 'uppercase',
                          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
                          zIndex: 2
                        }}>
                          {friendBelt.name}
                        </div>
                      </div>
                    );
                  })()}
                  <p style={{
                    fontWeight: 'bold',
                    color: '#f9fafb',
                    fontSize: '18px',
                    marginBottom: '6px',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}>
                    {friend.username || friend.displayName || 'Friend'}
                  </p>
                  <p style={{
                    fontSize: '16px',
                    color: (BELT_RANKS.find(b => b.name === friendStats[friend.uid]?.beltName) || getBeltForSessions(0)).color,
                    marginBottom: '6px',
                    fontWeight: 'bold',
                    minHeight: '22px',
                    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000'
                  }}>
                    {friendStats[friend.uid]?.sessionCount !== undefined ? `${friendStats[friend.uid].sessionCount} sessions` : '—'}
                  </p>
                  <p style={{ fontSize: '12px', color: '#f9fafb', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Tap to view profile</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      {showLogoutModal && (
        <div onClick={() => { playButtonClick(); setShowLogoutModal(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#1f2937', border: '2px solid #374151', borderRadius: '16px', padding: '24px', maxWidth: '420px', width: '100%', textAlign: 'center', position: 'relative' }}>
            <button
              onClick={() => { playButtonClick(); setShowLogoutModal(false); }}
              style={{ position: 'absolute', right: '12px', top: '10px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#f9fafb' }}
              aria-label="Close"
            >
              ×
            </button>
            <h3 style={{ color: '#f9fafb', fontWeight: 'bold', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000', marginBottom: '12px' }}>
              Log out?
            </h3>
            <p style={{ color: '#f9fafb', marginBottom: '16px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
              Are you sure you want to log out?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  playButtonClick();
                  setShowLogoutModal(false);
                  onLogout();
                }}
                style={{ flex: 1, padding: '12px 16px', backgroundColor: '#22c55e', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
              >
                Yes
              </button>
              <button
                onClick={() => { playButtonClick(); setShowLogoutModal(false); }}
                style={{ flex: 1, padding: '12px 16px', backgroundColor: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
      {showInfoModal && (
        <div onClick={() => { playButtonClick(); setShowInfoModal(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#1f2937', border: '2px solid #374151', borderRadius: '16px', padding: '24px', maxWidth: '520px', width: '100%', textAlign: 'center' }}>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <h3 style={{ color: '#f9fafb', fontWeight: 'bold', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Welcome to FitBudly</h3>
              <button
                onClick={() => { playButtonClick(); setShowInfoModal(false); }}
                style={{ position: 'absolute', right: 0, top: 0, background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#f9fafb' }}
                aria-label="Close"
              >
                ✖
              </button>
            </div>
            <p style={{ color: '#f9fafb', marginBottom: '12px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
              FitBudly helps you log workouts, track sessions, and see your progress over time.
            </p>
            <p style={{ color: '#f9fafb', marginBottom: '12px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
              Tap <strong>New Session</strong> to record workouts, intensities, and build streaks.
            </p>
            <p style={{ color: '#f9fafb', marginBottom: '12px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
              Your belt rank updates automatically as session count increases. Check <strong>Ranks</strong> to see the thresholds.
            </p>
            <p style={{ color: '#f9fafb', marginBottom: '16px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
              Add friends to compare progress, view profiles, and stay motivated together.
            </p>
            <button
              onClick={() => { playButtonClick(); setShowInfoModal(false); }}
              style={{ padding: '10px 16px', backgroundColor: '#3b82f6', color: '#ffffff', borderRadius: '8px', fontWeight: 'bold', border: 'none', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
      {showAddModal && (
        <div onClick={() => { playButtonClick(); setShowAddModal(false); setSearchResult(null); setAddError(''); setAddSuccess(''); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#1f2937', border: '2px solid #374151', borderRadius: '12px', padding: '16px', width: '100%', maxWidth: '420px' }}>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <h3 style={{ color: '#f9fafb', fontWeight: 'bold', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Add Friend</h3>
              <button
                onClick={() => { playButtonClick(); setShowAddModal(false); setSearchResult(null); setAddError(''); setAddSuccess(''); }}
                style={{ position: 'absolute', right: 0, top: 0, background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#f9fafb' }}
              >
                ✖
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search by username"
                value={addUsername}
                onChange={(e) => { setAddUsername(e.target.value); setAddError(''); setAddSuccess(''); }}
                style={{ width: '100%', padding: '12px 16px', border: '2px solid #4b5563', borderRadius: '8px', backgroundColor: '#374151', color: '#ffffff', textAlign: 'center' }}
              />
              <button
                onClick={async () => {
                  playButtonClick();
                  try {
                    setIsSearching(true);
                    const res = await findUserByUsername(addUsername);
                    setSearchResult(res);
                    if (!res) setAddError('User not found'); else setAddError('');
                  } catch (err) {
                    setAddError(err.message || 'Search failed');
                  } finally {
                    setIsSearching(false);
                  }
                }}
                disabled={isSearching}
                style={{ width: '100%', padding: '12px 16px', backgroundColor: isSearching ? '#9ca3af' : '#3b82f6', color: '#ffffff', borderRadius: '8px', fontWeight: 'bold', border: 'none', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000', cursor: isSearching ? 'not-allowed' : 'pointer' }}
              >
                {isSearching ? 'Searching...' : 'Search'}
              </button>
            </div>
            {searchResult && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px', border: '2px solid #374151', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <img src={searchResult.profileImage || defaultProfileImage} alt={searchResult.username || 'User'} style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
                  <div>
                    <p style={{ fontWeight: 'bold', color: '#f9fafb', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>{searchResult.username || searchResult.displayName || 'User'}</p>
                    <p style={{ fontSize: '12px', color: '#f9fafb', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>{searchResult.email || ''}</p>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    playButtonClick();
                    try {
                      setIsAddingFriend(true);
                      await sendRequest(addUsername);
                      setAddSuccess('Request sent');
                      setAddUsername('');
                      setSearchResult(null);
                    } catch (err) {
                      setAddError(err.message || 'Failed to send');
                    } finally {
                      setIsAddingFriend(false);
                    }
                  }}
                  disabled={isAddingFriend}
                  style={{ padding: '8px 12px', backgroundColor: isAddingFriend ? '#9ca3af' : '#22c55e', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 'bold', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000', cursor: isAddingFriend ? 'not-allowed' : 'pointer' }}
                >
                  {isAddingFriend ? 'Adding...' : 'Add'}
                </button>
              </div>
            )}
            {addError && <p style={{ color: '#ef4444', marginTop: '8px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>{addError}</p>}
            {addSuccess && <p style={{ color: '#22c55e', marginTop: '8px', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>{addSuccess}</p>}
            {friendsError && <p style={{ color: '#ef4444', marginTop: '8px', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>{friendsError}</p>}
          </div>
        </div>
      )}

      {showReqModal && (
        <div onClick={() => { playButtonClick(); setShowReqModal(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#1f2937', border: '2px solid #374151', borderRadius: '12px', padding: '16px', width: '100%', maxWidth: '480px' }}>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <h3 style={{ color: '#f9fafb', fontWeight: 'bold', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Requests</h3>
              <button
                onClick={() => { playButtonClick(); setShowReqModal(false); }}
                style={{ position: 'absolute', right: 0, top: 0, background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: '#f9fafb' }}
              >
                ✖
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {incomingRequests.length === 0 && (
                <p style={{ color: '#f9fafb', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>No pending requests</p>
              )}
              {incomingRequests.map((req) => (
                <div key={req.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px', border: '2px solid #374151', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img src={req.profileImage || defaultProfileImage} alt={req.fromUsername} style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
                    <p style={{ fontWeight: 'bold', color: '#f9fafb', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>{req.fromUsername}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { playButtonClick(); acceptRequest(req); }} style={{ padding: '8px 12px', backgroundColor: '#22c55e', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 'bold', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>✓</button>
                    <button onClick={() => { playButtonClick(); declineRequest(req); }} style={{ padding: '8px 12px', backgroundColor: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '8px', fontWeight: 'bold', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>✗</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showRanksModal && (
        <div onClick={() => { playButtonClick(); setShowRanksModal(false); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: '#1f2937', borderRadius: '16px', padding: '24px', maxWidth: '420px', width: '100%' }}>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <h2 style={{ color: '#f9fafb', fontWeight: 'bold', textAlign: 'center', textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>Belt Ranks</h2>
              <button
                onClick={() => { playButtonClick(); setShowRanksModal(false); }}
                style={{ position: 'absolute', right: 0, top: 0, background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#f9fafb' }}
              >
                ✖
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {BELT_RANKS.map((belt) => {
                const unlocked = userSessions >= belt.threshold;
                return (
                  <div key={belt.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '10px 12px', borderRadius: '10px', border: '2px solid #374151', backgroundColor: '#111827' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '80px', height: '10px', borderRadius: '6px', backgroundColor: belt.color, border: '2px solid #000000', position: 'relative' }}>
                        <span style={{ position: 'absolute', right: '6px', top: '0', bottom: '0', width: '12px', backgroundColor: belt.stripe, borderLeft: '2px solid #000000' }} />
                      </div>
                      <div>
                        <div style={{ color: '#f9fafb', fontWeight: 'bold' }}>{belt.name}</div>
                        <div style={{ color: '#9ca3af', fontSize: '12px' }}>{belt.threshold}+ sessions</div>
                      </div>
                    </div>
                    <div style={{ fontSize: '18px' }}>{unlocked ? '✅' : '🔒'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}